import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { bookingsAPI } from '../services/api';
import {
  Calendar,
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';

const Dashboard = () => {
  // Fetch dashboard stats
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['booking-stats'],
    queryFn: () => bookingsAPI.getStats({
      date_from: new Date().toISOString().split('T')[0],
      date_to: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    })
  });

  const stats = statsData?.data?.data?.stats || {};
  const todaysBookings = statsData?.data?.data?.todays_bookings || [];

  const statCards = [
    {
      title: 'Total Bookings',
      value: stats.total_bookings || 0,
      icon: Calendar,
      color: 'text-blue-600',
      bg: 'bg-blue-100',
    },
    {
      title: 'Confirmed Today',
      value: stats.confirmed_bookings || 0,
      icon: CheckCircle,
      color: 'text-green-600',
      bg: 'bg-green-100',
    },
    {
      title: 'Pending Confirmations',
      value: stats.pending_confirmations || 0,
      icon: AlertCircle,
      color: 'text-yellow-600',
      bg: 'bg-yellow-100',
    },
    {
      title: 'Total Revenue',
      value: `$${parseFloat(stats.total_revenue || 0).toFixed(2)}`,
      icon: TrendingUp,
      color: 'text-purple-600',
      bg: 'bg-purple-100',
    },
  ];

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return <span className="badge-confirmed">Confirmed</span>;
      case 'pending':
        return <span className="badge-pending">Pending</span>;
      case 'cancelled':
        return <span className="badge-cancelled">Cancelled</span>;
      default:
        return <span className="badge bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  if (statsLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">
            Welcome to the bus booking management portal
          </p>
        </div>
        <div className="animate-pulse grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card p-5">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">
          Welcome to the bus booking management portal
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <div key={stat.title} className="card p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`p-3 rounded-md ${stat.bg}`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {stat.title}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stat.value}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Today's Bookings */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Today's Departures
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Bookings scheduled for {format(new Date(), 'MMMM d, yyyy')}
          </p>
        </div>

        <div className="overflow-x-auto">
          {todaysBookings.length === 0 ? (
            <div className="px-6 py-8 text-center">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No departures today</h3>
              <p className="mt-1 text-sm text-gray-500">
                No bookings are scheduled for today.
              </p>
            </div>
          ) : (
            <table className="table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Time</th>
                  <th className="table-header">Route</th>
                  <th className="table-header">Customer</th>
                  <th className="table-header">Reference</th>
                  <th className="table-header">Passengers</th>
                  <th className="table-header">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {todaysBookings.map((booking) => (
                  <tr key={booking.id}>
                    <td className="table-cell">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 text-gray-400 mr-2" />
                        {booking.departure_time}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">
                          {booking.origin} → {booking.destination}
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm font-medium text-gray-900">
                        {booking.customer_name}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-gray-500 font-mono">
                        {booking.booking_reference}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center">
                        <Users className="h-4 w-4 text-gray-400 mr-1" />
                        {booking.total_passengers}
                      </div>
                    </td>
                    <td className="table-cell">
                      {getStatusBadge(booking.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertCircle className="h-8 w-8 text-yellow-400" />
            </div>
            <div className="ml-5">
              <h3 className="text-lg font-medium text-gray-900">
                Pending Confirmations
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {stats.pending_confirmations || 0} bookings awaiting confirmation
              </p>
              <div className="mt-3">
                <a href="/bookings?status=pending" className="text-sm text-primary-600 hover:text-primary-500">
                  Review pending bookings →
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Calendar className="h-8 w-8 text-blue-400" />
            </div>
            <div className="ml-5">
              <h3 className="text-lg font-medium text-gray-900">
                Schedule Management
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Manage routes and schedules
              </p>
              <div className="mt-3">
                <a href="/schedules" className="text-sm text-primary-600 hover:text-primary-500">
                  Manage schedules →
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-green-400" />
            </div>
            <div className="ml-5">
              <h3 className="text-lg font-medium text-gray-900">
                Customer Database
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                View customer information and history
              </p>
              <div className="mt-3">
                <a href="/customers" className="text-sm text-primary-600 hover:text-primary-500">
                  Browse customers →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;